import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { selectIsFavorite, toggleFavorite } from '../../ducks/favorites.duck';

import IconHeart from '../../containers/TopbarContainer/Topbar/icons/IconHeart';

import css from './FavoriteButton.module.css';

/**
 * Heart toggle overlaid on the listing hero image and listing cards.
 * Authenticated users persist favorites to their profile via the
 * favorites duck; guests are stored in localStorage and synced on
 * the next login (see [[favorites-duck]]).
 *
 * @param {Object} props
 * @param {Object} props.listingId – Sharetribe UUID instance (or string).
 * @returns {JSX.Element}
 */
const FavoriteButton = props => {
  const { listingId, className } = props;
  const dispatch = useDispatch();
  const intl = useIntl();

  const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
  const isFavorite = useSelector(state => selectIsFavorite(state, id));
  const inProgress = useSelector(state => state.favorites.toggleInProgressId === id);

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(toggleFavorite(id)).catch(() => {
      // Toggle errors surface via redux state if needed; swallow here
      // so the click handler doesn't bubble into an unhandled rejection.
    });
  };

  return (
    <button
      type="button"
      className={classNames(css.root, { [css.active]: isFavorite }, className)}
      onClick={handleClick}
      disabled={inProgress}
      aria-pressed={isFavorite}
      aria-label={intl.formatMessage({
        id: isFavorite ? 'FavoriteButton.removeAriaLabel' : 'FavoriteButton.addAriaLabel',
      })}
    >
      <IconHeart className={css.icon} />
    </button>
  );
};

export default FavoriteButton;
